"use client";
import React, { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import styles from "./CustomImage.module.scss";
import classnames from "classnames";

function toDataURL(url, callback) {
    var xhr = new XMLHttpRequest();
    xhr.onload = function () {
        var reader = new FileReader();
        reader.onloadend = function () {
            callback(reader.result);
        };
        reader.readAsDataURL(xhr.response);
    };
    xhr.open("GET", url);
    xhr.responseType = "blob";
    xhr.send();
}

let cachedBlurDataUrl;

export default function CustomImage({ src, alt = src || "image", width, height, sizes, loadingSpinner = true, loading, style, fill, blurDataUrl, children, ...rest }) {
    const [imgSrc, setImgSrc] = useState(src);
    const [showRealPic, setShowRealPic] = useState(false);

    useEffect(() => {
        setImgSrc(src);
    }, [src]);

    useEffect(() => {
        if (!cachedBlurDataUrl && blurDataUrl) {
            toDataURL(blurDataUrl, function (dataUrl) {
                cachedBlurDataUrl = dataUrl;
            });
        }
    }, []);

    const onError = useCallback(() => {
        setShowRealPic(true);
        setImgSrc("/static/images/image-load-failed.png");
    }, []);

    const onLoad = useCallback(() => {
        setShowRealPic(true);
    }, []);

    const ActualImage = Image.default || Image;

    return (
        <div
            style={{
                position: fill ? "absolute" : "relative",
                width: width || "100%",
                height: height || "100%",
            }}
        >
            <ActualImage
                priority={true}
                width={width}
                height={height}
                sizes={sizes}
                style={{
                    objectFit: "contain",
                    display: showRealPic || loading ? "block" : "none",
                    ...style,
                }}
                src={imgSrc}
                onLoad={onLoad}
                onError={onError}
                alt={alt}
                fill={fill}
                {...rest}
            />
            {children}
            {loadingSpinner && (!showRealPic || loading) && (
                <div
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <div className={classnames(styles["card"], styles["is-loading"])}>
                        <div className={styles["image"]} />
                    </div>
                </div>
            )}
        </div>
    );
}

const DEFAULT_MAX_WIDTH_HEIGHT = 80;

export function CustomImageNew({
    src = null,
    alt = src || "image",
    loadingSpinner = true,
    loading,
    style,
    blurDataUrl,
    children,
    maxWidth = DEFAULT_MAX_WIDTH_HEIGHT,
    fixedWidth,
    fixedHeight,
    fill,
    sizes,
    ...rest
}) {
    const [imgSrc, setImgSrc] = useState(src);
    const [showRealPic, setShowRealPic] = useState(false);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        setImgSrc(src);
    }, [src]);

    useEffect(() => {
        if (src) {
            const img = new window.Image();
            img.onload = function () {
                let { width, height } = img;

                const maxSize = maxWidth;
                const scale = Math.min(maxSize / width, maxSize / height, 1); // Never scale up

                setDimensions({
                    width: Math.round(width * scale),
                    height: Math.round(height * scale),
                });
            };
            img.onerror = () => {
                // Fallback to default icon if preload fails
                setDimensions({
                    width: maxWidth,
                    height: maxWidth,
                });
                setImgSrc("/static/images/image-load-failed.png");
            };
            img.src = src;
        }
    }, [src]);

    const onError = useCallback(() => {
        setShowRealPic(true);
        setImgSrc("/static/images/image-load-failed.png");
    }, []);

    const onLoad = useCallback(() => {
        setShowRealPic(true);
    }, []);

    const widthProperty = fill ? "100%" : fixedWidth || dimensions.width;
    const heightProperty = fill ? "100%" : fixedHeight || dimensions.height;

    const ActualImage = Image.default || Image;

    return (
        <div
            style={{
                position: fill ? "absolute" : "relative",
                width: widthProperty,
                height: heightProperty,
            }}
        >
            {imgSrc && (
                <ActualImage
                    src={imgSrc}
                    alt={alt}
                    unoptimized={imgSrc.endsWith(".svg")}
                    width={fill ? undefined : widthProperty}
                    height={fill ? undefined : heightProperty}
                    priority
                    onLoad={onLoad}
                    onError={onError}
                    sizes={sizes}
                    style={{
                        objectFit: "contain",
                        display: showRealPic || loading ? "block" : "none",
                        ...style,
                    }}
                    fill={fill}
                    {...rest}
                />
            )}

            {children}

            {loadingSpinner && imgSrc && (!showRealPic || loading) && (
                <div
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <div className={classnames(styles["card"], styles["is-loading"])}>
                        <div className={styles["image"]} />
                    </div>
                </div>
            )}
        </div>
    );
}
